import type { ContractFile } from '@gmn-portal/backend';
import { FileText, Trash2, Upload } from 'lucide-react';
import { DateTime } from 'luxon';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  payloadClient,
  useContractFiles,
  useDeleteContractFile,
  useUploadContractFile,
} from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import { DetailCard } from '@/shared/components/layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@/shared/components/ui';
import { resolveFileUploadErrorMessage } from '@/shared/utils';

async function openProtectedFile(url: string, filename?: string) {
  const request = await payloadClient.createRequest(url.replace('/api', ''), { method: 'GET' });
  const response = await payloadClient.fetcher(request);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = objectUrl;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  if (filename) {
    link.download = filename;
  }
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 500);
}

type ContractFilesTabProps = {
  contractId: string;
  canManageFiles: boolean;
};

export function ContractFilesTab({ contractId, canManageFiles }: ContractFilesTabProps) {
  const { t, locale } = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileToDelete, setFileToDelete] = useState<ContractFile | null>(null);

  const { data, isLoading } = useContractFiles({
    where: {
      contract: {
        equals: contractId,
      },
    },
    sort: '-createdAt',
    limit: 100,
    disabled: !contractId,
  });
  const uploadContractFile = useUploadContractFile();
  const deleteContractFile = useDeleteContractFile();

  const files = (data?.docs ?? []) as ContractFile[];

  const handleUpload = async (file: File) => {
    try {
      await uploadContractFile.mutateAsync({
        file,
        data: {
          contract: contractId,
        },
      });
      toast.success(t('contracts.files.uploadSuccess'));
    } catch (error) {
      toast.error(resolveFileUploadErrorMessage(error, t, 'contracts.files.uploadError'));
    }
  };

  const handleDelete = async () => {
    if (!fileToDelete) {
      return;
    }

    try {
      await deleteContractFile.mutateAsync({ id: fileToDelete.id });
      toast.success(t('contracts.files.deleteSuccess'));
      setFileToDelete(null);
    } catch {
      toast.error(t('contracts.files.deleteError'));
    }
  };

  return (
    <>
      <DetailCard icon={FileText} title={t('contracts.files.title')} className="lg:col-span-2">
        <div className="space-y-4" data-testid="contract-files-tab">
          <div className="text-sm text-muted-foreground">{t('contracts.files.description')}</div>
          {isLoading ? (
            <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
          ) : files.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-muted/30 p-5 text-sm text-muted-foreground">
              {t('contracts.files.empty')}
            </div>
          ) : (
            <div className="space-y-3">
              {files.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between gap-4 rounded-lg border border-border bg-background px-4 py-3"
                  data-testid={`contract-file-row-${file.id}`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                      <FileText className="size-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">
                        {file.filename || t('common.file')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {file.createdAt
                          ? DateTime.fromISO(file.createdAt).toFormat('dd.MM.yyyy HH:mm', {
                              locale,
                            })
                          : '-'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        file.url && openProtectedFile(file.url, file.filename || undefined)
                      }
                      disabled={!file.url}
                    >
                      {t('contracts.files.download')}
                    </Button>
                    {canManageFiles ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('contracts.files.delete')}
                        onClick={() => setFileToDelete(file)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}

          {canManageFiles ? (
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">{t('contracts.files.uploadHint')}</div>
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadContractFile.isPending}
                data-testid="contract-files-upload"
              >
                <Upload className="mr-2 size-4" />
                {t('contracts.files.upload')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    void handleUpload(file);
                  }
                  event.currentTarget.value = '';
                }}
                disabled={uploadContractFile.isPending}
              />
            </div>
          ) : null}
        </div>
      </DetailCard>

      <AlertDialog open={!!fileToDelete} onOpenChange={(open) => !open && setFileToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contracts.files.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('contracts.files.deleteConfirmation', {
                name: fileToDelete?.filename || t('common.file'),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDelete()}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
